import React, { useState, useEffect, useRef } from 'react';
import './StatCards.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const ValeurCards = ({ data, icon, color = "purple" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const getColorClasses = (baseColor) => {
    const colorMap = {
      blue: {
        bg: 'from-blue-500 to-blue-600',
        hover: 'hover:from-blue-600 hover:to-blue-700',
        iconBg: 'bg-blue-100',
        iconText: 'text-blue-600',
        border: 'border-blue-200'
      },
      purple: {
        bg: 'from-purple-500 to-purple-600',
        hover: 'hover:from-purple-600 hover:to-purple-700',
        iconBg: 'bg-purple-100',
        iconText: 'text-purple-600',
        border: 'border-purple-200'
      },
      pink: {
        bg: 'from-pink-500 to-pink-600',
        hover: 'hover:from-pink-600 hover:to-pink-700',
        iconBg: 'bg-pink-100',
        iconText: 'text-pink-600',
        border: 'border-pink-200'
      },
      green: {
        bg: 'from-green-500 to-green-600',
        hover: 'hover:from-green-600 hover:to-green-700',
        iconBg: 'bg-green-100',
        iconText: 'text-green-600',
        border: 'border-green-200'
      }
    };
    return colorMap[baseColor] || colorMap.purple;
  };

  const colors = getColorClasses(color);

  const scrollRef = useRef(null);

  const handlePrev = () => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8;
    el.scrollBy({ left: -amount, behavior: 'smooth' });
  };

  const handleNext = () => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8;
    el.scrollBy({ left: amount, behavior: 'smooth' });
  };

  return (
    <section className="py-4 w-full" ref={sectionRef}>
      <div className="w-full px-4 relative">
        {/* Navigation buttons */}
        <button onClick={handlePrev} aria-label="Previous" className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center hover:scale-105 transition-transform">
          <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button onClick={handleNext} aria-label="Next" className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center hover:scale-105 transition-transform">
          <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <div ref={scrollRef} className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth py-6 md:py-8 px-2 md:px-6 no-scrollbar">
          {data.map((mission, index) => (
            <div key={index} className={`snap-center flex-shrink-0 w-[280px] md:w-[320px] lg:w-[350px]`}>
              <div
                className={`group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border ${colors.border} transform hover:-translate-y-2 cursor-pointer ${isVisible ? 'opacity-100 animate-fade-in-up' : 'opacity-0'}`}
                style={{ animationDelay: `${index * 0.12}s`, minHeight: '320px' }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${colors.bg} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
                <div className={`h-2 w-full bg-gradient-to-r ${colors.bg}`} />
                <div className="p-6 relative z-10">
                  <div className="flex justify-center mb-6">
                    {mission.phote ? (
                      <div className="relative">
                        <div className={`absolute inset-0 bg-gradient-to-br ${colors.bg} rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-500`} />
                        <div className="relative w-24 h-24 rounded-full bg-cover bg-center shadow-lg ring-4 ring-white group-hover:scale-110 transition-transform duration-500" style={{ backgroundImage: `url(${mission.phote})` }} />
                      </div>
                    ) : (
                      <div className="relative">
                        <div className={`absolute inset-0 bg-gradient-to-br ${colors.bg} rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-500`} />
                        <div className={`relative w-20 h-20 ${colors.iconBg} rounded-full flex items-center justify-center shadow-lg ring-4 ring-white group-hover:scale-110 group-hover:rotate-12 transition-all duration-500`}>
                          <FontAwesomeIcon icon={icon} className={`text-4xl ${colors.iconText}`} />
                        </div>
                      </div>
                    )}
                  </div>

                  <h3 className="text-xl font-bold text-gray-800 text-center mb-4 leading-tight group-hover:text-gray-900 transition-colors">{mission.title}</h3>
                  {mission.description && (
                    <p className="text-gray-600 text-center text-sm leading-relaxed line-clamp-4 group-hover:text-gray-700 transition-colors">{mission.description}</p>
                  )}

                  <div className="mt-6 flex justify-center">
                    <div className={`h-1 w-16 bg-gradient-to-r ${colors.bg} rounded-full group-hover:w-24 transition-all duration-500`} />
                  </div>

                  <div className={`absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 ${colors.border} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                  <div className={`absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 ${colors.border} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                </div>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white via-transparent to-transparent opacity-10" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .animate-fade-in-up {
          animation: fadeInUp 0.7s ease-out forwards;
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .line-clamp-4 { display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden; }
        /* Hide horizontal scrollbar while keeping scroll functionality */
        .no-scrollbar {
          -ms-overflow-style: none; /* IE and Edge */
          scrollbar-width: none; /* Firefox */
          overscroll-behavior-x: contain; /* prevent scroll chaining/bounce */
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none; /* Safari and Chrome */
          height: 0;
        }
      `}</style>
    </section>
  );
};

export default ValeurCards; 